import {
  endAgendaItemManualRedirection,
  endBroadcastsManualRedirection,
  startAgendaItemManualRedirection,
  startBroadcastsManualRedirection
} from '@goldcast/api/backend';
import { GenericHeaderNotification } from 'uicomponents';

import {
  BUFFER_TIME_AFTER_SIMULIVE_TESTING_ENDS,
  getCloudFrontUrl,
  MIN_SIMULIVE_PLAYBACK_START_DELAY
} from '@/constants';
import DialogService from '@/services/dialogs/DialogService';
import loggerService from '@/services/logger.service';
import { ActivityType, handleSpeakerActivity } from '@/services/speakerActivity.service';
import { debounceUpdateTextQADetails } from '@/services/textQNA.service';
import store from '@/store';
import {
  ManualSessionRedirectionType,
  formatAgenda,
  mapTracksToTickets,
  setButtonLoading,
  setIsLiveAndTrigger
} from '@/store/modules/event/actions';
import { Booth, BoothTiers, TicketType, Widget } from '@/store/modules/event/types';
import { pushRoute } from '@/utils/routerAccess';

export function gotoNextPage(context, name: string) {
  let page: any;

  if (name) {
    page = { name };
  } else {
    const shouldGoToAgenda = () => {
      const noOfAgendaItem = context.rootGetters['event/currentEventAgenda']?.length;

      if (context.getters.isWebinar || noOfAgendaItem === 1) {
        return false;
      }

      const noOfRecordedAgendaItem = context.rootGetters['event/allOnDemandBroadcasts']?.length;

      if (context.state.event.on_demand_mode && noOfRecordedAgendaItem) {
        return true;
      }

      return true;
    };

    page = {
      name: 'EventLanding',
      query: {
        next: shouldGoToAgenda() ? 'EventAgendaPage' : undefined
      }
    };
  }

  pushRoute(page);
}

export async function onConfirm(context, { sessionId, sessionType, actionType }: ManualSessionRedirectionType) {
  if (sessionType === 'BROADCAST') {
    if (actionType === 'start') {
      await performSessionAction(context, sessionId, actionType, () =>
        handleSpeakerActivity(
          () => startBroadcastsManualRedirection({ id: sessionId }),
          ActivityType.SessionStart,
          ActivityType.SessionStartFailed
        )
      );
    } else if (actionType === 'end') {
      await performSessionAction(context, sessionId, actionType, () =>
        handleSpeakerActivity(
          () => endBroadcastsManualRedirection({ id: sessionId }),
          ActivityType.SessionEnd,
          ActivityType.SessionEndFailed
        )
      );
    }
  } else {
    const parsedSessionId = parseInt(sessionId);
    if (parsedSessionId) {
      if (actionType === 'start') {
        await performSessionAction(context, sessionId, actionType, () =>
          handleSpeakerActivity(
            () => startAgendaItemManualRedirection({ id: parsedSessionId }),
            ActivityType.SessionStart,
            ActivityType.SessionStartFailed
          )
        );
      } else if (actionType === 'end') {
        await performSessionAction(context, sessionId, actionType, () =>
          handleSpeakerActivity(
            () => endAgendaItemManualRedirection({ id: parsedSessionId }),
            ActivityType.SessionEnd,
            ActivityType.SessionEndFailed
          )
        );
      }
    }
  }
}

export async function performSessionAction(context, sessionId, actionType, actionFunction) {
  try {
    setButtonLoading(context, sessionId, actionType, true);
    await actionFunction();
    // context.commit('updateAgendaItem', response);
  } catch (error) {
    if (error && (error as any).message) {
      DialogService.show(
        GenericHeaderNotification,
        {
          text: (error as any).message
        },
        {
          hideOverlay: true,
          cancelOnClickOutside: false,
          blockBackgroundActions: false,
          closeOnCTAClick: false,
          autoCancelTimeout: 3000
        }
      );
    }
  } finally {
    setButtonLoading(context, sessionId, actionType, false);
  }
}

export function saveData(
  context,
  eventUsersLoaded,
  data: {
    event: any[];
    broadcast: any[];
    agenda: any[];
    tracks: any[];
    booth: any[];
    resources: any[];
    discussion_groups: any;
    organization: any;
    widgets: Widget[];
    speakers: any[];
    organizers: any[];
    staffs: any[];
    speaker_stage_requests: any[];
    ticket_types: TicketType[];
    primary_booths: Record<string, BoothTiers>;
    secondary_booths: Record<string, BoothTiers>;
    pricing: Record<string, any>;
  }
) {
  const event = data?.event?.pop();

  if (event) {
    context.commit('setEvent', { event });
    context.commit('setPricingConfig', data?.pricing || {});

    (context.commit as any)('event/avroom/updateJitterDelay', 10, {
      root: true
    });
    context.dispatch('event/checkBoothTabAndRedirect', event, {
      root: true
    });
    context.dispatch('event/initOrganization', data.organization, {
      root: true
    });

    context.commit('setTicketTypes', data.ticket_types);
    context.dispatch('sidebar/openDriftTabIfDefault', null, { root: true });

    const { primary_booths, secondary_booths } = data;

    Object.values(primary_booths)?.forEach(boothByCategory => {
      const { booths: boothsByTier } = boothByCategory;

      boothsByTier?.sort((booth1, booth2) => booth1.rank - booth2.rank);
    });

    Object.values(secondary_booths)?.forEach(boothByCategory => {
      const { booths: boothsByTier } = boothByCategory;

      boothsByTier?.sort((booth1, booth2) => booth1.rank - booth2.rank);
    });

    context.commit('setPrimaryBooths', primary_booths);
    context.commit('setSecondaryBooths', secondary_booths);

    context
      .dispatch('makeFormattedBooths', data?.booth || [])
      .then((formattedBooths: Booth[]) => {
        context.commit('setBooths', formattedBooths);
      })
      .catch(err => {
        loggerService.error('Error while formatting booth data!', err);
      });

    context.commit('setEmbeddedWidgets', data.widgets);

    context.commit('setDataLoaded', true);

    context.dispatch('loadFonts', context.state.event);
    context.dispatch('setLocale', context.state.event);
    context.commit('setThemeStyles', context.rootState);
    context.dispatch('mediaroom/checkUserPreferencesAndSetFilter', null, {
      root: true
    });
    context.commit('setResources', data?.resources || []);

    context.commit('setDiscussionGroups', {
      discussionGroups: data.discussion_groups?.data || []
    });
    if (data.discussion_groups?.data) {
      context.dispatch('mattermost/getDiscussionGroupWithoutLoader', event, {
        root: true
      });
    }
    context.commit(
      'filterBar/setPageAsLoaded',
      {
        page: 'DiscussionGroups',
        count: data.discussion_groups?.count || data.discussion_groups?.length || 0,
        totalCount: data.discussion_groups?.count || data.discussion_groups?.length || 0
      },
      {
        root: true
      }
    );

    context.dispatch('setRawSpeakersFromUber', { users: data?.speakers || [] });
    context.dispatch('setRawSpeakersFromUber', { users: data?.organizers || [], type: 'organizers' });
    context.commit('addPartialUsersFromUber', {
      users: [...data.speakers, ...data.organizers, ...data.staffs].map(it => it.user),
      event: event.id
    });
    setTimeout(() => {
      debounceUpdateTextQADetails();
    });
    // context.dispatch("setSessionVariablesForRelicx");

    return eventUsersLoaded
      .then(([user]) => {
        context.commit(
          'setTracks',
          mapTracksToTickets(
            (data?.tracks || []).map(it => {
              if (
                !it.agenda_items ||
                (Array.isArray(it.agenda_items) && it.agenda_items.length === 0) ||
                it.agenda_items === ''
              ) {
                loggerService.error('Track has empty agenda_items at parse time', {
                  trackId: it.id,
                  trackTitle: it.title
                });
              }
              return {
                ...it,
                label: it.title,
                value: it.id,
                agenda_items: (it.agenda_items || '').split(',')
              };
            }),
            data.ticket_types,
            user as any
          )
        );

        const agendaItems = formatAgenda(
          {
            items: data?.agenda || [],
            broadcasts: data?.broadcast
          },
          context.state.event.tracks,
          context.state.event.users
        );
        agendaItems.items.forEach(item => setIsLiveAndTrigger(context, item));
        agendaItems.broadcasts.forEach(item => setIsLiveAndTrigger(context, item));
        context.commit('setEventAgenda', agendaItems);

        (context.commit as any)(
          'filterBar/setPageAsLoaded',
          {
            page: 'EventAgendaPage',
            count: context.getters.currentEventAgenda.length,
            totalCount: context.getters.currentEventAgenda.length,
            setCurrentPage: false
          },
          { root: true }
        );

        context.commit('setSpeakerStageRequests', data.speaker_stage_requests);

        const currentSlideAsset = data?.broadcast?.find((b: any) => b.current_playing_slide_asset);
        if (currentSlideAsset && context.rootState.event.useNewSlideAnimation) {
          context.commit('setNewSlideAnimationAssets', {
            id: currentSlideAsset.current_playing_slide_asset || null,
            current_playing_slide_number: currentSlideAsset.current_playing_slide_number ?? 0,
            current_playing_step_number: currentSlideAsset.current_playing_step_number ?? 0,
            slide_state_version: currentSlideAsset.slide_state_version || 1,
            action: currentSlideAsset.action
          });
        }

        return event;
      })
      .catch(err => {
        loggerService.error('Failed to get current event_user for tracks', err);
        return event;
      });
  }

  return Promise.reject({ statusCode: 404 });
}

export function getTotalVideoDurationOfBroadcast(broadcast: any) {
  let totalSimuliveVideosDuration = 0;
  const vidAssets = broadcast?.video_assets;

  if (Array.isArray(vidAssets)) {
    vidAssets.forEach(video => {
      totalSimuliveVideosDuration += video.duration;
    });
  }

  return totalSimuliveVideosDuration * 1000;
}

export function getSessionEndTimeBasedOnTotalVideoDuration(broadcast: any, durationOfAllVideoAssets: number = 0) {
  const startTime = broadcast?.live_start_time ? new Date(broadcast?.live_start_time) : new Date();

  if (broadcast.broadcast_type !== 'SIMULIVE') {
    return startTime;
  }

  return new Date(startTime.getTime() + durationOfAllVideoAssets + MIN_SIMULIVE_PLAYBACK_START_DELAY);
}
export function getSimuliveTestRunEndTime(broadcast: any) {
  const testRunEndTime = broadcast?.simulive_test_run_end_time
    ? new Date(broadcast.simulive_test_run_end_time)
    : new Date();
  const testRunEndTimeStamp = testRunEndTime.getTime();

  const toForceWatchInHouseSimulive = store.getters['simuliveroom/shouldUseInhouseSimulive'];
  const isForceWatchingInHouseSimulive = broadcast?.broadcast_type === 'SIMULIVE' && toForceWatchInHouseSimulive;
  const startDelay = isForceWatchingInHouseSimulive ? 0 : MIN_SIMULIVE_PLAYBACK_START_DELAY;
  return new Date(testRunEndTimeStamp - BUFFER_TIME_AFTER_SIMULIVE_TESTING_ENDS + startDelay);
}

export function getSimuliveTestVideoStartTime(testRunEndTime: Date, durationOfAllVideoAssets: number = 0) {
  const videoPlaybackEndTimeStamp = testRunEndTime.getTime();

  return new Date(videoPlaybackEndTimeStamp - durationOfAllVideoAssets);
}

export function isCloudFrontURL(url: string): boolean {
  const goldcastRegex = /^https?:\/\/([a-zA-Z0-9_-]+\.)?staticassets\.goldcast\.io\//i;
  const cloudfrontRegex = /^https?:\/\/([a-zA-Z0-9_-]+\.)?cloudfront\.net\//i;
  const goldcastEURegex = /^https?:\/\/([a-zA-Z0-9_-]+\.)?staticassets\.eu\.goldcast\.io\//i;
  return goldcastRegex.test(url) || cloudfrontRegex.test(url) || goldcastEURegex.test(url);
}

export function getProfileImage(profilePictureUrl: string) {
  return profilePictureUrl ? `${getCloudFrontUrl('CLOUDFRONT_URL')}/${profilePictureUrl}` : null;
}
